import type { TypedSupabaseClient } from '../client';
import type { TablesUpdate } from '../../../supabase-client/src/database.types';
import type {
  IWithholdingRepository,
  RSOperationType,
  WithholdingCertificate,
  WithholdingCertificateLine,
  WithholdingCertificateFilters,
  CreateWithholdingCertificateInput,
  UpdateWithholdingCertificateInput,
  WithholdingDeclaration,
  WithholdingDeclarationFilters,
  CreateWithholdingDeclarationInput,
} from '../../../interfaces/src';
import { buildDocumentNumber } from '@erp/domain/numbering';
import { resolveDocNumberYear } from './doc-number-year';
import { fetchAllRows } from './fetch-all-rows';
import { sortByLineNumber } from './document-line-order';

const CERT_SEQ: Record<'sale' | 'purchase', string> = {
  sale: 'seq_withholding_certificate_sale',
  purchase: 'seq_withholding_certificate_purchase',
};
const CERT_PREFIX: Record<'sale' | 'purchase', string> = {
  sale: 'RSV',
  purchase: 'RSA',
};

export class SupabaseWithholdingRepository implements IWithholdingRepository {
  // cast en any en attendant la régénération des types Supabase pour les nouvelles tables
  private readonly supabase: any;
  constructor(client: TypedSupabaseClient) {
    this.supabase = client;
  }

  // ---------- Operation Types ----------

  async listOperationTypes(filters?: {
    applicable?: 'sale' | 'purchase';
  }): Promise<RSOperationType[]> {
    let q = this.supabase
      .from('rs_operation_types')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (filters?.applicable === 'sale') q = q.eq('applies_to_sale', true);
    if (filters?.applicable === 'purchase')
      q = q.eq('applies_to_purchase', true);

    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []).map(mapOperationType);
  }

  async getOperationTypeByCode(code: string): Promise<RSOperationType | null> {
    const { data, error } = await this.supabase.rpc(
      'get_rs_operation_type_by_code',
      { p_code: code },
    );
    if (error) throw error;
    if (!data || (Array.isArray(data) && data.length === 0)) return null;
    const row = Array.isArray(data) ? data[0] : data;
    return row ? mapOperationType(row) : null;
  }

  // ---------- Certificates ----------

  async listCertificates(
    filters?: WithholdingCertificateFilters,
  ): Promise<WithholdingCertificate[]> {
    const fetch = (withSources: boolean) =>
      fetchAllRows<Record<string, unknown>>((from, to) => {
        const select = withSources
          ? '*, withholding_certificate_lines(*, rs_operation_types(*)), withholding_certificate_sources(source_document_id)'
          : '*, withholding_certificate_lines(*, rs_operation_types(*))';
        let q = this.supabase.from('withholding_certificates').select(select);
        if (filters?.certificateType)
          q = q.eq('certificate_type', filters.certificateType);
        if (filters?.thirdPartyId)
          q = q.eq('third_party_id', filters.thirdPartyId);
        if (filters?.dateFrom) q = q.gte('date_payment', filters.dateFrom);
        if (filters?.dateTo) q = q.lte('date_payment', filters.dateTo);
        if (filters?.declarationId === null) q = q.is('declaration_id', null);
        else if (filters?.declarationId)
          q = q.eq('declaration_id', filters.declarationId);
        if (filters?.isPaid === true) q = q.not('payment_id', 'is', null);
        else if (filters?.isPaid === false) q = q.is('payment_id', null);
        return q
          .order('created_at', { ascending: false })
          .order('id', { ascending: false })
          .range(from, to);
      });

    // Tente avec la jointure sources ; fallback sans si la table n'existe pas
    // encore (migration 20260423230500 pas appliquée → PGRST200).
    try {
      return (await fetch(true)).map(mapCertificate);
    } catch (error) {
      if (!isMissingSourcesTable(error)) throw error;
      return (await fetch(false)).map(mapCertificate);
    }
  }

  async getCertificateById(id: string): Promise<WithholdingCertificate | null> {
    const buildQuery = (withSources: boolean) => {
      const select = withSources
        ? '*, withholding_certificate_lines(*, rs_operation_types(*)), withholding_certificate_sources(source_document_id)'
        : '*, withholding_certificate_lines(*, rs_operation_types(*))';
      return this.supabase
        .from('withholding_certificates')
        .select(select)
        .eq('id', id)
        .single();
    };
    const first = await buildQuery(true);
    if (first.error && isMissingSourcesTable(first.error)) {
      const second = await buildQuery(false);
      if (second.error) {
        if (second.error.code === 'PGRST116') return null;
        throw second.error;
      }
      return mapCertificate(second.data);
    }
    if (first.error) {
      if (first.error.code === 'PGRST116') return null;
      throw first.error;
    }
    return mapCertificate(first.data);
  }

  async getCertificateBySourceId(
    sourceDocumentId: string,
  ): Promise<WithholdingCertificate | null> {
    // Une facture peut historiquement être liée à plusieurs retenues (anciens
    // doublons ou tests). On prend la plus récente au lieu de crasher en
    // "multiple rows" comme le ferait .single() / .maybeSingle().
    // Essaye d'abord la table de jonction (schéma "multi-source").
    const { data: links, error: linkErr } = await this.supabase
      .from('withholding_certificate_sources')
      .select('certificate_id')
      .eq('source_document_id', sourceDocumentId)
      .limit(1);
    if (!linkErr && links && links.length > 0) {
      return this.getCertificateById(links[0]!.certificate_id);
    }
    if (linkErr && !isMissingSourcesTable(linkErr)) throw linkErr;
    // Fallback vers le champ scalaire historique.
    const { data, error } = await this.supabase
      .from('withholding_certificates')
      .select('*, withholding_certificate_lines(*, rs_operation_types(*))')
      .eq('source_document_id', sourceDocumentId)
      .order('created_at', { ascending: false })
      .limit(1);
    if (error) throw error;
    return data && data.length > 0 ? mapCertificate(data[0]) : null;
  }

  async createCertificate(
    input: CreateWithholdingCertificateInput,
  ): Promise<WithholdingCertificate> {
    const ids = input.sourceDocumentIds.filter(Boolean);
    for (const sourceId of ids) {
      const existing = await this.getCertificateBySourceId(sourceId);
      if (existing) {
        throw new Error(
          'Une retenue a la source existe deja pour cette facture.',
        );
      }
    }
    if (ids.length === 0) {
      throw new Error(
        'Au moins une facture source est requise pour créer une retenue à la source.',
      );
    }

    const number = await this.getNextCertificateNumber(input.certificateType);
    const { data: doc, error: docError } = await this.supabase
      .from('withholding_certificates')
      .insert({
        number,
        certificate_type: input.certificateType,
        third_party_type: input.thirdPartyType,
        third_party_id: input.thirdPartyId,
        source_document_id: ids[0],
        date_payment: input.datePayment,
        ref_certif: input.refCertif,
        total_ht: input.totalHt,
        total_vat: input.totalVat,
        total_ttc: input.totalTtc,
        total_ras: input.totalRas,
        total_net_servi: input.totalNetServi,
        notes: input.notes,
      })
      .select()
      .single();
    if (docError) throw docError;

    // Liens vers toutes les factures sources — best-effort : si la table
    // de jonction n'existe pas encore (migration 20260423230500 pas appliquée),
    // on tombe dans l'ancien mode scalaire où seule la première facture est
    // conservée. Le code front fera le fallback via sourceDocumentId scalar.
    const sourceRows = ids.map((sid) => ({
      certificate_id: doc.id,
      source_document_id: sid,
    }));
    const { error: srcErr } = await this.supabase
      .from('withholding_certificate_sources')
      .insert(sourceRows);
    if (srcErr && !isMissingSourcesTable(srcErr)) throw srcErr;
    if (srcErr && ids.length > 1) {
      console.warn(
        '[withholding] table withholding_certificate_sources absente — retenue créée mais limitée à la première facture. Appliquez la migration 20260423230500_withholding_multi_source.sql.',
      );
    }

    if (input.lines.length > 0) {
      const rows = input.lines.map((l, idx) => ({
        certificate_id: doc.id,
        line_number: idx + 1,
        id_type_operation: l.idTypeOperation,
        operation_type_id: l.operationTypeId ?? null,
        annee_facturation: l.anneeFacturation,
        cnpc: l.cnpc,
        p_charge: l.pCharge,
        montant_ht: l.montantHt,
        taux_rs: l.tauxRs,
        taux_tva: l.tauxTva,
        montant_tva: l.montantTva,
        montant_ttc: l.montantTtc,
        montant_rs: l.montantRs,
        montant_net_servi: l.montantNetServi,
      }));
      const { error } = await this.supabase
        .from('withholding_certificate_lines')
        .insert(rows);
      if (error) throw error;
    }

    if (input.certificateType === 'sale') {
      await this.applySaleCertificateToSourceDocuments(ids, input);
    }

    return (await this.getCertificateById(doc.id))!;
  }

  async updateCertificate(
    id: string,
    input: UpdateWithholdingCertificateInput,
  ): Promise<WithholdingCertificate> {
    const mapped: TablesUpdate<'withholding_certificates'> = {};
    if (input.datePayment !== undefined)
      mapped['date_payment'] = input.datePayment;
    if (input.refCertif !== undefined) mapped['ref_certif'] = input.refCertif;
    if (input.notes !== undefined) mapped['notes'] = input.notes;
    if (input.paymentId !== undefined) mapped['payment_id'] = input.paymentId;
    if (input.declarationId !== undefined)
      mapped['declaration_id'] = input.declarationId;

    if (Object.keys(mapped).length > 0) {
      const { error } = await this.supabase
        .from('withholding_certificates')
        .update(mapped)
        .eq('id', id);
      if (error) throw error;
    }
    return (await this.getCertificateById(id))!;
  }

  async deleteCertificate(id: string): Promise<void> {
    // Ventes : supprimer la retenue rend sa déduction au document — le net à
    // payer redevient le TTC plein (règle « pas de RAS sans retenue créée »).
    // La restitution se fait AVANT le DELETE : si le DELETE échoue, recommencer
    // la suppression ne restitue rien deux fois (garde withholding_amount > 0).
    const cert = await this.getCertificateById(id).catch(() => null);
    if (cert?.certificateType === 'sale') {
      await this.revertSaleCertificateFromSourceDocuments(
        cert.sourceDocumentIds,
      );
    }
    const { error } = await this.supabase
      .from('withholding_certificates')
      .delete()
      .eq('id', id);
    if (error) throw error;
  }

  /**
   * VENTES — matérialise la retenue sur les documents sources du certificat.
   *
   * Règle métier (13/08/2026) : un document de vente ne porte JAMAIS de RAS à
   * la saisie — il reste au TTC plein. C'est la création du certificat qui
   * déduit : `withholding_amount` = RS de la facture, net à payer diminué
   * d'autant. Un document portant DÉJÀ une retenue (héritage d'avant cette
   * règle, où la RAS se déduisait à la saisie) est laissé tel quel — pas de
   * double déduction.
   *
   * Les achats ne passent pas ici : une facture d'achat calcule sa RAS au
   * formulaire (c'est nous qui retenons, le papier fournisseur fait foi).
   */
  private async applySaleCertificateToSourceDocuments(
    sourceIds: string[],
    input: CreateWithholdingCertificateInput,
  ): Promise<void> {
    // RS par document : une ligne de certificat par facture, dans l'ordre des
    // sources — les deux créateurs (bouton « Créer RAS » de la facture, alerte
    // RAS de l'encaissement) construisent les lignes ainsi. Mono-source : le
    // total du certificat.
    const rasBySource = new Map<string, number>();
    if (sourceIds.length === 1) {
      rasBySource.set(sourceIds[0]!, round3(input.totalRas));
    } else if (input.lines.length === sourceIds.length) {
      sourceIds.forEach((sid, i) =>
        rasBySource.set(sid, round3(input.lines[i]!.montantRs)),
      );
    } else {
      console.warn(
        '[withholding] lignes/sources désalignées — déduction RAS non appliquée aux documents sources.',
      );
      return;
    }

    const { data, error } = await this.supabase
      .from('authorized_sales_documents' as 'sales_documents')
      .select('id, total_ttc, stamp_amount, withholding_amount, net_to_pay')
      .in('id', sourceIds);
    if (error) throw error;

    for (const row of (data ?? []) as Array<Record<string, unknown>>) {
      const docId = row['id'] as string;
      const rs = rasBySource.get(docId) ?? 0;
      const alreadyWithheld = Number(row['withholding_amount']) || 0;
      if (rs <= 0 || alreadyWithheld > 0) continue;
      // Base = net stocké (préserve un net de BL ajusté à la main) ; à défaut,
      // TTC + timbre (documents anciens sans net persisté).
      const storedNet = Number(row['net_to_pay']) || 0;
      const stamp = Math.max(Number(row['stamp_amount']) || 0, 0);
      const base =
        storedNet > 0
          ? storedNet
          : round3((Number(row['total_ttc']) || 0) + stamp);
      const { error: updError } = await this.supabase
        .from('sales_documents')
        .update({ withholding_amount: rs, net_to_pay: round3(base - rs) })
        .eq('id', docId);
      if (updError) throw updError;
    }
  }

  /**
   * VENTES — retire la déduction RAS des documents sources (suppression du
   * certificat). Le montant restitué est la retenue PORTÉE PAR LE DOCUMENT
   * (pas celle du certificat) : c'est exactement ce qui avait été déduit, y
   * compris pour un document d'avant la règle, déduit à la saisie avec ses
   * propres arrondis.
   */
  private async revertSaleCertificateFromSourceDocuments(
    sourceIds: string[],
  ): Promise<void> {
    if (sourceIds.length === 0) return;
    const { data, error } = await this.supabase
      .from('authorized_sales_documents' as 'sales_documents')
      .select('id, withholding_amount, net_to_pay')
      .in('id', sourceIds);
    if (error) throw error;
    for (const row of (data ?? []) as Array<Record<string, unknown>>) {
      const withheld = Number(row['withholding_amount']) || 0;
      if (withheld <= 0) continue;
      const net = Number(row['net_to_pay']) || 0;
      const { error: updError } = await this.supabase
        .from('sales_documents')
        .update({ withholding_amount: 0, net_to_pay: round3(net + withheld) })
        .eq('id', row['id'] as string);
      if (updError) throw updError;
    }
  }

  async getNextCertificateNumber(type: 'sale' | 'purchase'): Promise<string> {
    const prefix = CERT_PREFIX[type];
    const seq = CERT_SEQ[type];
    const year = await resolveDocNumberYear(this.supabase);
    const { data, error } = await this.supabase.rpc('get_next_sequence_value', {
      seq_name: seq,
    });
    if (!error && data != null) {
      return buildDocumentNumber(prefix, year, Number(data));
    }
    const { count } = await this.supabase
      .from('withholding_certificates')
      .select('*', { count: 'exact', head: true })
      .eq('certificate_type', type);
    const next = (count ?? 0) + 1;
    return buildDocumentNumber(prefix, year, next);
  }

  async getNextOperationSequence(): Promise<number> {
    const { data, error } = await this.supabase.rpc('get_next_sequence_value', {
      seq_name: 'seq_withholding_operation',
    });
    if (!error && data != null) return Number(data);
    // Fallback : count + 1 (non atomique mais OK en dev)
    const { count } = await this.supabase
      .from('withholding_certificate_lines')
      .select('*', { count: 'exact', head: true });
    return (count ?? 0) + 1;
  }

  // ---------- Declarations ----------

  async listDeclarations(
    filters?: WithholdingDeclarationFilters,
  ): Promise<WithholdingDeclaration[]> {
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) => {
      let q = this.supabase
        .from('withholding_declarations')
        .select(
          '*, withholding_certificates(*, withholding_certificate_lines(*, rs_operation_types(*)))',
        );
      if (filters?.declarationType)
        q = q.eq('declaration_type', filters.declarationType);
      if (filters?.anneeDepot) q = q.eq('annee_depot', filters.anneeDepot);
      if (filters?.moisDepot) q = q.eq('mois_depot', filters.moisDepot);
      if (filters?.isExported !== undefined)
        q = q.eq('is_exported', filters.isExported);
      return q
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to);
    });
    return rows.map(mapDeclaration);
  }

  async getDeclarationById(id: string): Promise<WithholdingDeclaration | null> {
    const { data, error } = await this.supabase
      .from('withholding_declarations')
      .select(
        '*, withholding_certificates(*, withholding_certificate_lines(*, rs_operation_types(*)))',
      )
      .eq('id', id)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return mapDeclaration(data);
  }

  async createDeclaration(
    input: CreateWithholdingDeclarationInput,
  ): Promise<WithholdingDeclaration> {
    const number = await this.getNextDeclarationNumber();
    const { data: doc, error } = await this.supabase
      .from('withholding_declarations')
      .insert({
        number,
        declaration_type: input.declarationType,
        acte_depot: input.acteDepot ?? 0,
        annee_depot: input.anneeDepot,
        mois_depot: input.moisDepot,
        notes: input.notes,
      })
      .select()
      .single();
    if (error) throw error;

    if (input.certificateIds.length > 0) {
      const { error: upErr } = await this.supabase
        .from('withholding_certificates')
        .update({ declaration_id: doc.id })
        .in('id', input.certificateIds);
      if (upErr) throw upErr;
    }

    return (await this.getDeclarationById(doc.id))!;
  }

  async deleteDeclaration(id: string): Promise<void> {
    await this.supabase
      .from('withholding_certificates')
      .update({ declaration_id: null })
      .eq('declaration_id', id);
    const { error } = await this.supabase
      .from('withholding_declarations')
      .delete()
      .eq('id', id);
    if (error) throw error;
  }

  async markDeclarationExported(
    id: string,
    xmlContent: string,
    declarantMatricule: string,
  ): Promise<void> {
    const { error } = await this.supabase
      .from('withholding_declarations')
      .update({
        is_exported: true,
        exported_at: new Date().toISOString(),
        tej_xml_content: xmlContent,
        declarant_matricule_fiscal: declarantMatricule,
      })
      .eq('id', id);
    if (error) throw error;
  }

  async getNextDeclarationNumber(): Promise<string> {
    const year = await resolveDocNumberYear(this.supabase);
    const { data, error } = await this.supabase.rpc('get_next_sequence_value', {
      seq_name: 'seq_withholding_declaration',
    });
    if (!error && data != null) {
      return buildDocumentNumber('DECL', year, Number(data));
    }
    const { count } = await this.supabase
      .from('withholding_declarations')
      .select('*', { count: 'exact', head: true });
    const next = (count ?? 0) + 1;
    return buildDocumentNumber('DECL', year, next);
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/**
 * PostgREST renvoie PGRST200 ou un message clair quand une relation n'existe
 * pas encore dans le cache de schéma. On utilise ça pour détecter le cas où
 * la migration 20260423230500_withholding_multi_source.sql n'a pas été
 * appliquée, et retomber gracieusement sur le comportement mono-source.
 */
function isMissingSourcesTable(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { code?: string; message?: string; details?: string };
  const haystack =
    `${e.code ?? ''} ${e.message ?? ''} ${e.details ?? ''}`.toLowerCase();
  return (
    e.code === 'PGRST200' ||
    e.code === '42P01' || // Postgres "undefined_table"
    haystack.includes('withholding_certificate_sources')
  );
}

// ─── Mappers ─────────────────────────────────────────────────────────────────

function mapOperationType(row: Record<string, unknown>): RSOperationType {
  return {
    id: row['id'] as string,
    code: row['code'] as string,
    labelFr: row['label_fr'] as string,
    labelAr: (row['label_ar'] as string) ?? null,
    labelEn: (row['label_en'] as string) ?? null,
    defaultRate: row['default_rate'] as number,
    threshold: (row['threshold'] as number) ?? null,
    appliesToSale: (row['applies_to_sale'] as boolean) ?? true,
    appliesToPurchase: (row['applies_to_purchase'] as boolean) ?? true,
    displayOrder: (row['display_order'] as number) ?? 100,
    isActive: (row['is_active'] as boolean) ?? true,
  };
}

function mapCertificate(row: Record<string, unknown>): WithholdingCertificate {
  const lines =
    (row['withholding_certificate_lines'] as
      | Record<string, unknown>[]
      | undefined) ?? [];
  const sources =
    (row['withholding_certificate_sources'] as
      | Record<string, unknown>[]
      | undefined) ?? [];
  const scalarSource = (row['source_document_id'] as string | null) ?? null;
  // Liste unique, dédoublonnée, en incluant le scalaire legacy s'il n'est pas déjà dans la jonction
  const sourceIds = Array.from(
    new Set([
      ...sources.map((s) => s['source_document_id'] as string),
      ...(scalarSource ? [scalarSource] : []),
    ]),
  );
  return {
    id: row['id'] as string,
    number: row['number'] as string,
    certificateType: row['certificate_type'] as 'sale' | 'purchase',
    thirdPartyType: row['third_party_type'] as 'client' | 'supplier',
    thirdPartyId: row['third_party_id'] as string,
    sourceDocumentId: scalarSource,
    sourceDocumentIds: sourceIds,
    datePayment: row['date_payment'] as string,
    refCertif: row['ref_certif'] as string,
    totalHt: (row['total_ht'] as number) ?? 0,
    totalVat: (row['total_vat'] as number) ?? 0,
    totalTtc: (row['total_ttc'] as number) ?? 0,
    totalRas: (row['total_ras'] as number) ?? 0,
    totalNetServi: (row['total_net_servi'] as number) ?? 0,
    declarationId: (row['declaration_id'] as string) ?? null,
    paymentId: (row['payment_id'] as string) ?? null,
    notes: (row['notes'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    // L'embed PostgREST n'a pas d'ORDER BY — voir sortByLineNumber.
    lines: sortByLineNumber(lines.map(mapLine)),
  };
}

function mapLine(row: Record<string, unknown>): WithholdingCertificateLine {
  const opTypeRow = row['rs_operation_types'] as
    | Record<string, unknown>
    | null
    | undefined;
  return {
    id: row['id'] as string,
    certificateId: row['certificate_id'] as string,
    lineNumber: row['line_number'] as number,
    idTypeOperation: row['id_type_operation'] as string,
    operationTypeId: (row['operation_type_id'] as string) ?? null,
    operationType: opTypeRow ? mapOperationType(opTypeRow) : undefined,
    anneeFacturation: row['annee_facturation'] as number,
    cnpc: (row['cnpc'] as number) ?? 0,
    pCharge: (row['p_charge'] as number) ?? 0,
    montantHt: row['montant_ht'] as number,
    tauxRs: row['taux_rs'] as number,
    tauxTva: (row['taux_tva'] as number) ?? 0,
    montantTva: (row['montant_tva'] as number) ?? 0,
    montantTtc: row['montant_ttc'] as number,
    montantRs: row['montant_rs'] as number,
    montantNetServi: row['montant_net_servi'] as number,
  };
}

function mapDeclaration(row: Record<string, unknown>): WithholdingDeclaration {
  const certs =
    (row['withholding_certificates'] as
      | Record<string, unknown>[]
      | undefined) ?? [];
  return {
    id: row['id'] as string,
    number: row['number'] as string,
    declarationType: row['declaration_type'] as 'sale' | 'purchase',
    acteDepot: row['acte_depot'] as number,
    anneeDepot: row['annee_depot'] as number,
    moisDepot: row['mois_depot'] as number,
    isExported: (row['is_exported'] as boolean) ?? false,
    exportedAt: (row['exported_at'] as string) ?? null,
    declarantMatriculeFiscal:
      (row['declarant_matricule_fiscal'] as string) ?? null,
    tejXmlContent: (row['tej_xml_content'] as string) ?? null,
    notes: (row['notes'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    certificates: certs.map(mapCertificate),
  };
}
